/**
 * SQL 빌더 유틸 — ORDER BY / LIMIT OFFSET
 *
 * @example
 * const orderSql = buildOrderBy(
 *   { name: "U.name", createdAt: "U.createAt" },
 *   data.sortKey,
 *   data.sortType,
 *   "U.id DESC"   // 기본 정렬 (sortKey 없을 때)
 * );
 * // → " ORDER BY U.name ASC"
 *
 * @example
 * const { sql: pageSql, values: pageValues } = buildPagination(data.page, data.pageSize);
 * // → { sql: " LIMIT ?, ?", values: [20, 20] }
 *
 * @example
 * const list = await queryAll(
 *   `SELECT * FROM users ${orderSql} ${pageSql}`,
 *   [...whereValues, ...pageValues]
 * );
 */

/** 정렬 컬럼 맵 — { [sortKey]: "실제컬럼명" } */
export type SortColumns = Record<string, string>;

/**
 * ORDER BY 절 생성
 * @param columns   sortKey → 실제 컬럼명 맵
 * @param sortKey   요청에서 받은 정렬 키
 * @param sortType  "ASC" | "DESC" (대소문자 무관)
 * @param defaultSql  sortKey가 없거나 맵에 없을 때 사용할 기본 정렬 SQL (컬럼명만, ORDER BY 제외)
 */
export function buildOrderBy(
    columns: SortColumns,
    sortKey: string | undefined,
    sortType: string | undefined,
    defaultSql = ""
): string {
    const col = sortKey ? columns[sortKey] : undefined;
    const direction =
        sortType?.toUpperCase() === "ASC" || sortType?.toUpperCase() === "DESC"
            ? sortType.toUpperCase()
            : undefined;

    if (col && direction) {
        return ` ORDER BY ${col} ${direction}`;
    }
    if (defaultSql) {
        return ` ORDER BY ${defaultSql}`;
    }
    return "";
}

/**
 * LIMIT / OFFSET 절 생성
 * page 또는 pageSize가 없으면 빈 문자열과 빈 배열을 반환한다.
 * @returns { sql, values } — values를 query params에 스프레드해서 사용
 */
export function buildPagination(
    page: number | undefined,
    pageSize: number | undefined
): { sql: string; values: number[] } {
    if (!page || !pageSize) {
        return { sql: "", values: [] };
    }
    const offset = (page - 1) * pageSize;
    return { sql: " LIMIT ?, ?", values: [offset, pageSize] };
}
